<div class="container">
  <div class="page-header">
    <h2>Transformation History</h2>
    <button class="btn-refresh" (click)="load()" [class.spinning]="loading">&#8635; Refresh</button>
  </div>

  <!-- Filters -->
  <div class="filter-bar">
    <div class="filter-group">
      <label for="filterStatus">Status</label>
      <select id="filterStatus" [(ngModel)]="filterStatus" (change)="load()">
        <option value="">All</option>
        <option value="Success">Success</option>
        <option value="Warning">Warning</option>
        <option value="PartialSuccess">Partial Success</option>
        <option value="Error">Error</option>
      </select>
    </div>
    <div class="filter-group">
      <label for="filterTemplateId">Template</label>
      <input
        id="filterTemplateId"
        type="text"
        [(ngModel)]="filterTemplateId"
        placeholder="Filter by template ID…"
        (keyup.enter)="load()"
      />
    </div>
    <div class="filter-group">
      <label for="filterLimit">Limit</label>
      <select id="filterLimit" [(ngModel)]="filterLimit" (change)="load()">
        <option [ngValue]="25">25</option>
        <option [ngValue]="50">50</option>
        <option [ngValue]="100">100</option>
        <option [ngValue]="250">250</option>
      </select>
    </div>
    <div class="filter-group">
      <label for="filterFrom">From</label>
      <input id="filterFrom" type="date" [(ngModel)]="filterFrom" (change)="load()" />
    </div>
    <div class="filter-group">
      <label for="filterTo">To</label>
      <input id="filterTo" type="date" [(ngModel)]="filterTo" (change)="load()" />
    </div>
  </div>

  <!-- Summary cards -->
  <div class="stat-cards">
    <div class="stat-card">
      <span class="stat-num">{{ total }}</span>
      <span class="stat-label">Total</span>
    </div>
    <div class="stat-card success">
      <span class="stat-num">{{ counts['Success'] || 0 }}</span>
      <span class="stat-label">Success</span>
    </div>
    <div class="stat-card warning">
      <span class="stat-num">{{ counts['Warning'] || 0 }}</span>
      <span class="stat-label">Warning</span>
    </div>
    <div class="stat-card partial">
      <span class="stat-num">{{ counts['PartialSuccess'] || 0 }}</span>
      <span class="stat-label">Partial</span>
    </div>
    <div class="stat-card error">
      <span class="stat-num">{{ counts['Error'] || 0 }}</span>
      <span class="stat-label">Error</span>
    </div>
    <div class="stat-card neutral">
      <span class="stat-num">{{ avgMs }}</span>
      <span class="stat-label">Avg ms</span>
    </div>
  </div>

  <div *ngIf="error" class="alert alert-error">{{ error }}</div>

  <!-- Grid -->
  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th></th>
          <th>Status</th>
          <th>Template</th>
          <th>Message</th>
          <th>Correlation ID</th>
          <th>Timestamp</th>
          <th>Duration</th>
          <th>Source</th>
          <th>Expires</th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let log of logs">
          <tr class="summary-row" (click)="toggleDetail(log)" [class.expanded]="selectedId === log.id">
            <td class="expand-cell">
              <span class="expand-icon">{{ selectedId === log.id ? '▼' : '▶' }}</span>
            </td>
            <td>
              <span class="badge" [ngClass]="statusClass(log.status)">{{ statusLabel(log.status) }}</span>
            </td>
            <td class="template-id-cell">
              {{ log.templateName || log.templateId }}
            </td>
            <td class="message-cell" [title]="log.messageSummary || ''">{{ log.messageSummary || '—' }}</td>
            <td class="correlation-cell">
              <code>{{ log.correlationId || '—' }}</code>
            </td>
            <td>{{ log.timestamp | date: 'MMM d, y HH:mm:ss' }}</td>
            <td class="ms-cell">{{ log.durationMs }} ms</td>
            <td>{{ log.source || '—' }}</td>
            <td class="muted">{{ log.expiresAt | date: 'mediumDate' }}</td>
          </tr>

          <!-- Expanded detail row -->
          <tr *ngIf="selectedId === log.id" class="detail-row">
            <td colspan="9">
              <div class="detail-panel" *ngIf="!detailLoading">
                <div *ngIf="detailError" class="alert alert-error">{{ detailError }}</div>

                <div class="detail-grid" *ngIf="detail">
                  <!-- Errors panel -->
                  <div *ngIf="parsedErrors.length > 0" class="detail-section error-section">
                    <div class="section-title error-title">Errors ({{ parsedErrors.length }})</div>
                    <div class="error-list">
                      <div *ngFor="let e of parsedErrors" class="error-item">
                        <strong>{{ e.errorCode }}</strong>
                        <span *ngIf="e.sourcePath" class="path-chip">{{ e.sourcePath }} → {{ e.fieldPath }}</span>
                        <span class="error-msg">{{ e.message }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Warnings panel -->
                  <div *ngIf="parsedWarnings.length > 0" class="detail-section warning-section">
                    <div class="section-title warning-title">Warnings ({{ parsedWarnings.length }})</div>
                    <div class="error-list">
                      <div *ngFor="let w of parsedWarnings" class="error-item">
                        <strong>{{ w.code }}</strong>
                        <span *ngIf="w.sourcePath" class="path-chip warning-chip"
                          >{{ w.sourcePath }} → {{ w.targetPath }}</span
                        >
                        <span class="error-msg">{{ w.message }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Input / Output JSON -->
                  <div class="detail-section">
                    <div class="section-title">Input JSON</div>
                    <pre class="json-block">{{ detail.inputData | prettyJson }}</pre>
                  </div>
                  <div class="detail-section">
                    <div class="section-title">Output JSON</div>
                    <pre class="json-block" *ngIf="detail.outputData">{{ detail.outputData | prettyJson }}</pre>
                    <span *ngIf="!detail.outputData" class="muted">No output produced</span>
                  </div>
                </div>
              </div>
              <div *ngIf="detailLoading" class="loading-detail">Loading…</div>
            </td>
          </tr>
        </ng-container>

        <tr *ngIf="logs.length === 0 && !loading">
          <td colspan="9" class="no-data">
            No transformation logs found. Run a transformation on the "Test Transform" tab to generate logs.
          </td>
        </tr>
        <tr *ngIf="loading">
          <td colspan="9" class="no-data">Loading…</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
